import { useState, useRef, useEffect } from 'react';
import { X, Camera, Barcode, Loader2 } from 'lucide-react';
import { indianFoodService } from '../services/indian-food.service';
import toast from 'react-hot-toast';

interface BarcodeScannerProps {
  onClose: () => void;
  onBarcodeDetected: (barcode: string, productData: Record<string, unknown>) => void;
}

export function BarcodeScanner({ onClose, onBarcodeDetected }: BarcodeScannerProps) {
  const [manualEntry, setManualEntry] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);

  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [stream]);

  const lookupBarcode = async (barcode: string) => {
    setLoading(true);
    setError(null);

    try {
      const data = await indianFoodService.lookupBarcode(barcode);
      if (data && typeof data === 'object') {
        const productData = {
          name: (data as Record<string, unknown>).name ?? 'Unknown Product',
          barcode,
          serving_size: (data as Record<string, unknown>).typicalServingSize ?? 100,
          calories: (data as Record<string, unknown>).caloriesPer100g ?? 0,
          carbs: (data as Record<string, unknown>).carbsPer100g ?? 0,
          protein: (data as Record<string, unknown>).proteinPer100g ?? 0,
          fat: (data as Record<string, unknown>).fatPer100g ?? 0,
          fiber: (data as Record<string, unknown>).fiberPer100g ?? 0,
        };
        onBarcodeDetected(barcode, productData);
      } else {
        setError('Product not found. You can add manually from the food database.');
        toast.error('Barcode not in database. Try adding food manually.');
      }
    } catch {
      setError('Unable to lookup barcode. Please try again.');
      toast.error('Barcode lookup failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = () => {
    if (barcodeInput.trim()) {
      lookupBarcode(barcodeInput.trim());
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Scan Barcode</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {!manualEntry && (
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-dropsugar-blue to-dropsugar-blue-dark rounded-2xl p-8 text-white text-center">
                <Barcode className="w-16 h-16 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Scan Product Barcode</h3>
                <p className="text-blue-100">
                  Quickly add packaged foods by entering their barcode number. Camera scanning coming
                  soon.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setManualEntry(true)}
                className="w-full bg-dropsugar-green text-white py-4 rounded-xl hover:bg-dropsugar-green-dark transition-colors font-medium flex items-center justify-center gap-2"
              >
                <Camera className="w-5 h-5" />
                <span>Enter Barcode Manually</span>
              </button>

              {error && (
                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}
            </div>
          )}

          {manualEntry && (
            <div>
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 text-white mb-6">
                <Barcode className="w-12 h-12 mb-3" />
                <h3 className="text-lg font-bold mb-2">Enter Barcode Number</h3>
                <p className="text-green-100 text-sm">
                  Type the barcode number found on the product package
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Barcode Number
                </label>
                <input
                  type="text"
                  value={barcodeInput}
                  onChange={(e) => setBarcodeInput(e.target.value)}
                  placeholder="e.g., 8901234567890"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-dropsugar-green focus:outline-none text-lg"
                  onKeyDown={(e) => e.key === 'Enter' && handleManualSubmit()}
                />
              </div>

              {loading && (
                <div className="bg-dropsugar-green/10 rounded-xl p-6 mb-4 border-2 border-dropsugar-green/30">
                  <div className="flex items-center justify-center gap-3">
                    <Loader2 className="w-6 h-6 text-dropsugar-green animate-spin" />
                    <p className="text-dropsugar-blue-dark font-medium">Looking up product...</p>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-4">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setManualEntry(false);
                    setBarcodeInput('');
                    setError(null);
                  }}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors font-medium"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleManualSubmit}
                  disabled={!barcodeInput.trim() || loading}
                  className="flex-1 px-6 py-3 bg-dropsugar-green text-white rounded-xl hover:bg-dropsugar-green-dark transition-colors font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Lookup Product
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
